import React, { useState, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { EntraService } from '../services/entraService';
import ImageCropDialog from '../../profile-picture-manager/components/ImageCropDialog';
import './ProfilePictureManager.css';

interface ProfilePictureManagerProps {
  onPictureUpdate?: (pictureUrl: string | null) => void;
}

export const ProfilePictureManager: React.FC<ProfilePictureManagerProps> = ({
  onPictureUpdate
}) => {
  const { instance, accounts } = useMsal();
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const entraService = new EntraService(instance as any);

  useEffect(() => {
    const loadPicture = async () => {
      if (accounts.length === 0) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const pictureUrl = await entraService.getProfilePicture();
        setProfilePicture(pictureUrl);
        onPictureUpdate?.(pictureUrl);
      } catch (err) {
        console.error('Failed to load profile picture:', err);
        setError('Failed to load profile picture');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPicture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const loadProfilePicture = async () => {
    if (accounts.length === 0) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const pictureUrl = await entraService.getProfilePicture();
      setProfilePicture(pictureUrl);
      onPictureUpdate?.(pictureUrl);
    } catch (err) {
      console.error('Failed to load profile picture:', err);
      setError('Failed to load profile picture');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 4MB for Graph API)
    if (file.size > 4 * 1024 * 1024) {
      setError('Image size must be less than 4MB');
      return;
    }

    setError(null);

    // Read the file as data URL for the cropper
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target?.result as string);
    };
    reader.onerror = () => {
      setError('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setImageSrc(null);

    // Create a File from the cropped blob
    const croppedFile = new File([croppedBlob], 'profile_picture.jpg', {
      type: 'image/jpeg',
    });

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target?.result as string);
    };
    reader.readAsDataURL(croppedFile);

    // Upload the cropped file
    uploadProfilePicture(croppedFile);
  };

  const handleCropCancel = () => {
    setImageSrc(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadProfilePicture = async (file: File) => {
    setIsLoading(true);
    setError(null);

    try {
      await entraService.uploadProfilePicture(file);
      await loadProfilePicture(); // Reload to get the new picture
      setPreviewImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      console.error('Failed to upload profile picture:', err);
      setError(err.message || 'Failed to upload profile picture');
      setPreviewImage(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await entraService.deleteProfilePicture();
      setProfilePicture(null);
      onPictureUpdate?.(null);
    } catch (err: any) {
      console.error('Failed to delete profile picture:', err);
      setError(err.message || 'Failed to delete profile picture');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="profile-picture-manager">
      <div className="picture-container">
        {isLoading ? (
          <div className="picture-placeholder loading">
            <div className="spinner"></div>
          </div>
        ) : previewImage ? (
          <img 
            src={previewImage} 
            alt="Preview" 
            className="profile-picture preview"
          />
        ) : profilePicture ? (
          <img 
            src={profilePicture} 
            alt="Profile" 
            className="profile-picture"
          />
        ) : (
          <div className="picture-placeholder">
            <svg 
              width="60" 
              height="60" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="picture-actions">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        <button
          className="btn-upload"
          onClick={handleUploadClick}
          disabled={isLoading}
        >
          {profilePicture ? 'Change Picture' : 'Upload Picture'}
        </button>
        
        {profilePicture && (
          <button
            className="btn-delete"
            onClick={handleDelete}
            disabled={isLoading}
          >
            Remove
          </button>
        )}
      </div>

      <div className="picture-info">
        <small>
          Upload a profile picture to personalize your documents.
          Maximum size: 4MB. Supported formats: JPG, JPEG, PNG, GIF.
        </small>
      </div>

      {/* Image Crop Dialog */}
      {imageSrc && (
        <ImageCropDialog
          open={true}
          imageSrc={imageSrc}
          onCancel={handleCropCancel}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};